import React from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import ScorePicker from "./ScorePicker";
import { STATUS_STYLE } from "../constants";

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.inactive;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.875,
        py: 0.25,
        borderRadius: "6px",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: s.color,
        bgcolor: s.bg,
        ml: 1,
        verticalAlign: "middle",
      }}
    >
      {s.label}
    </Box>
  );
}

export default function TechnicianEvaluationTable({
  technicians,
  rows,
  criteria,
  onChange,
  onSave,
  savingId,
}) {
  const techColWidth = criteria.length >= 2 ? "20%" : "28%";
  const criteriaWidth = `${Math.floor(60 / Math.max(criteria.length, 1))}%`;

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table sx={{ width: "100%", tableLayout: "fixed", minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx(techColWidth)}>
              Técnico
            </TableCell>
            {criteria.map((c) => (
              <TableCell key={c.id} sx={headCellSx(criteriaWidth)}>
                {c.shortLabel}
                <Typography component="span" sx={{ ml: 0.5, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                  (0–10)
                </Typography>
              </TableCell>
            ))}
            <TableCell sx={headCellSx("18%")}>Observaciones</TableCell>
            <TableCell sx={headCellSx(56)} />
          </TableRow>
        </TableHead>
        <TableBody>
          {technicians.map((tech) => {
            const row = rows[tech._id] || {};
            const isInactive = tech.status === "inactive";
            const saving = savingId === tech._id;

            return (
              <TableRow
                key={tech._id}
                sx={{
                  opacity: isInactive ? 0.65 : 1,
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover td": { bgcolor: "#fafbff" },
                  transition: "background 0.12s",
                }}
              >
                <TableCell sx={bodyCellSx}>
                  <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.9rem" }}>
                    {tech.name}
                    <StatusBadge status={tech.status} />
                  </Typography>
                  {tech.zone && (
                    <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 0.25 }}>
                      {tech.zone}
                    </Typography>
                  )}
                </TableCell>
                {criteria.map((c) => (
                  <TableCell key={c.id} sx={bodyCellSx}>
                    <ScorePicker
                      value={row[c.field] ?? 0}
                      onChange={(v) => onChange(tech._id, c.field, v)}
                      compact
                      accent={c.color}
                    />
                  </TableCell>
                ))}
                <TableCell sx={bodyCellSx}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Opcional"
                    value={row.notes ?? ""}
                    onChange={(e) => onChange(tech._id, "notes", e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontSize: "0.85rem",
                        "& fieldset": { borderColor: "#e8eaef" },
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  <Tooltip title="Guardar evaluación">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onSave(tech._id)}
                        disabled={saving}
                        sx={{
                          bgcolor: "#1975d1",
                          color: "#fff",
                          width: 34,
                          height: 34,
                          "&:hover": { bgcolor: "#1565c0" },
                          "&.Mui-disabled": { bgcolor: "#e3f2fd", color: "#90caf9" },
                        }}
                      >
                        {saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const headCellSx = (width) => ({
  fontWeight: 600,
  fontSize: "0.7rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#9ca3af",
  borderBottom: "1px solid #e8eaef",
  py: 1.5,
  width,
  bgcolor: "#fafbfc",
});

const bodyCellSx = {
  borderBottom: "1px solid #f3f4f6",
  py: 2,
  verticalAlign: "top",
};
